/**
 * DataLoader - Handles loading and parsing data from API responses
 */
export class DataLoader {
    constructor() {
        this.imageCache = new Map(); // Cache for loaded images
    }

    /**
     * Parse API response data into internal structure
     * @param {Object} apiData - Raw API response
     * @returns {Object} Parsed data structure with galleryId and sceneId
     */
    parseApiData(apiData) {
        console.log('[DataLoader] Parsing API data...');

        // Handle new API structure where data is nested in 'config'
        let dataSource = apiData;
        if (apiData.config && apiData.config.pictures) {
            console.log('[DataLoader] Detected nested config structure');
            dataSource = apiData.config;
        }

        if (!dataSource || !dataSource.pictures) {
            throw new Error('Invalid API data: missing pictures array');
        }

        const parsed = {
            pictures: this.parsePictures(dataSource.pictures),
            graph: dataSource.graph || null,
            // Include galleryId and sceneId from root level if available
            galleryId: apiData.galleryId || null,
            sceneId: apiData.metadata?.sceneId || null,
            galleryPath: apiData.galleryPath || null,
            // Include project title/name
            title: dataSource.title || apiData.title || null,
        };

        console.log('[DataLoader] Parsed data:', parsed);
        return parsed;
    }

    /**
     * Parse pictures array from API
     * @param {Array} pictures - Pictures array from API
     * @returns {Array} Parsed pictures
     */
    parsePictures(pictures) {
        if (!Array.isArray(pictures)) {
            console.error('[DataLoader] Pictures is not an array:', pictures);
            return [];
        }

        return pictures.map(pic => {
            const parsed = {
                id: pic.id,
                filename: pic.filename,
                listing_id: pic.listing_id,
                name: pic.name || pic.filename,
                url: pic.url,
                thumbnail_url: pic.thumbnail_url || null, // Thumbnail URL (WebP format)
                created_at: pic.created_at,
                updated_at: pic.updated_at,
                // Support for rotation and brightness from API
                initialRotation: pic.initialRotation || 0,
                brightness: pic.brightness || 100,
            };

            // Debug log to verify thumbnail_url is present
            if (!parsed.thumbnail_url) {
                console.warn(`[DataLoader] ⚠️ Missing thumbnail_url for picture:`, pic.name || pic.filename);
                console.warn(`  Raw API data for this picture:`, pic);
            }

            return parsed;
        });
    }

    /**
     * Load an image from URL with caching
     * @param {string} url - Image URL
     * @param {string} cacheKey - Cache key (usually picture id)
     * @returns {Promise<HTMLImageElement|string>} Loaded image or URL (for KTX2)
     */
    async loadImage(url, cacheKey) {
        // Check cache first
        if (this.imageCache.has(cacheKey)) {
            console.log(`[DataLoader] Image cache hit: ${cacheKey}`);
            return this.imageCache.get(cacheKey);
        }

        console.log(`[DataLoader] Loading image from URL: ${url}`);

        // For KTX2 files, return URL directly (TextureLoader will handle loading)
        const isKTX2 = url.toLowerCase().endsWith('.ktx2');
        if (isKTX2) {
            console.log(`[DataLoader] KTX2 detected, returning URL directly: ${cacheKey}`);
            // Cache the URL
            this.imageCache.set(cacheKey, url);
            return url;
        }

        // For standard images, use Image element
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous'; // For CORS

            img.onload = () => {
                console.log(`[DataLoader] Image loaded successfully: ${cacheKey}`);
                this.imageCache.set(cacheKey, img);
                resolve(img);
            };

            img.onerror = (error) => {
                console.error(`[DataLoader] Failed to load image: ${url}`, error);
                reject(new Error(`Failed to load image from ${url}`));
            };

            img.src = url;
        });
    }

    /**
     * Load multiple images in parallel with progress tracking
     * @param {Array} pictures - Array of picture objects with url property
     * @param {Function} onProgress - Progress callback (current, total)
     * @returns {Promise<Array>} Array of loaded images
     */
    async loadImages(pictures, onProgress = null) {
        console.log(`[DataLoader] Loading ${pictures.length} images...`);

        const promises = pictures.map(async (picture, index) => {
            try {
                const img = await this.loadImage(picture.url, picture.id.toString());

                if (onProgress) {
                    onProgress(index + 1, pictures.length);
                }

                return {
                    picture,
                    image: img,
                    success: true,
                };
            } catch (error) {
                console.error(`[DataLoader] Failed to load image for picture ${picture.id}:`, error);

                if (onProgress) {
                    onProgress(index + 1, pictures.length);
                }

                return {
                    picture,
                    image: null,
                    success: false,
                    error: error.message,
                };
            }
        });

        const results = await Promise.all(promises);

        const successCount = results.filter(r => r.success).length;
        console.log(`[DataLoader] Loaded ${successCount}/${pictures.length} images successfully`);

        return results;
    }

    /**
     * Convert loaded image to Blob for browser caching
     * @param {HTMLImageElement} img - Image element
     * @returns {Promise<Blob>} Image blob
     */
    async imageToBlob(img) {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/jpeg', 0.95);
        });
    }

    /**
     * Clear image cache
     */
    clearCache() {
        console.log('[DataLoader] Clearing image cache');
        this.imageCache.clear();
    }

    /**
     * Get cache size
     * @returns {number} Number of cached images
     */
    getCacheSize() {
        return this.imageCache.size;
    }
}
